import { createContext, useContext, useMemo, useState, useCallback } from "react";
import { trpc } from "@/lib/trpc";

export type TenantConfig = {
  id: number;
  nome: string;
  slug: string;
  subdomain: string;
  logoUrl: string | null;
  logoNegativoUrl: string | null;
  primaryColor: string;
  homeModule: string | null;
  modules: string[];
  networks: string[];
};

type TenantContextValue = {
  tenant: TenantConfig | null;
  isLoading: boolean;
  /** true quando o host é hub.razconsulting.com.br (ou dev com devClientId=null) */
  isHub: boolean;
  /** true quando o host não tem subdomínio de cliente (razconsulting.com.br raiz) */
  isMaster: boolean;
  /** Em ambiente dev/preview, permite trocar o cliente ativo manualmente */
  setDevClientId: (id: number | null) => void;
  devClientId: number | null;
  isDev: boolean;
  /** Subdomínio detectado no hostname (null em dev/hub/raiz) */
  subdomain: string | null;
};

const DEV_CLIENT_KEY = "RAZ_DEV_CLIENT_ID";

const TenantContext = createContext<TenantContextValue>({
  tenant: null,
  isLoading: false,
  isHub: false,
  isMaster: false,
  setDevClientId: () => {},
  devClientId: null,
  isDev: false,
  subdomain: null,
});

function isDevEnvironment(): boolean {
  const host = window.location.hostname;
  return host === "localhost" || host.endsWith(".manus.computer") || host.endsWith(".manus.space");
}

/**
 * Extrai o subdomínio de cliente do hostname atual.
 *
 * Mapeamento de produção:
 *   emiteai.razconsulting.com.br  → "emiteai"   (tenant EmiteAí)
 *   mc.razconsulting.com.br       → "mc"         (tenant MC Intelligence)
 *   abia.razconsulting.com.br    → "abia"      (tenant ABIA)
 *   hub.razconsulting.com.br      → "hub"        (RAZ Hub — acesso total)
 *   razconsulting.com.br          → null         (raiz — redireciona para hub)
 *   localhost / *.manus.*         → null         (dev — usa override)
 */
function extractSubdomain(): string | null {
  const host = window.location.hostname;
  if (host === "localhost" || host.endsWith(".manus.computer") || host.endsWith(".manus.space")) {
    return null;
  }
  const parts = host.split(".");
  // razconsulting.com.br = 3 partes; subdomínio = 4+
  if (parts.length >= 4) {
    return parts[0].toLowerCase();
  }
  return null;
}

export function TenantProvider({ children }: { children: React.ReactNode }) {
  const subdomain = useMemo(() => extractSubdomain(), []);
  const isDev = useMemo(() => isDevEnvironment(), []);

  // "hub" é o subdomínio especial para admins RAZ com acesso total
  const isHub = subdomain === "hub";
  // Sem subdomínio = domínio raiz (razconsulting.com.br) ou dev sem override
  const isMaster = subdomain === null;

  // Dev override: lê do localStorage
  const [devClientId, setDevClientIdState] = useState<number | null>(() => {
    if (typeof window === "undefined") return null;
    const stored = localStorage.getItem(DEV_CLIENT_KEY);
    return stored ? Number(stored) : null;
  });

  const setDevClientId = useCallback((id: number | null) => {
    if (id === null) {
      localStorage.removeItem(DEV_CLIENT_KEY);
    } else {
      localStorage.setItem(DEV_CLIENT_KEY, String(id));
    }
    setDevClientIdState(id);
  }, []);

  // Quando há subdomínio de cliente (não "hub"), busca o tenant
  const isClientSubdomain = subdomain !== null && !isHub;
  const { data: tenantBySubdomain, isLoading: loadingSubdomain } = trpc.clients.getBySubdomain.useQuery(
    { subdomain: subdomain! },
    { enabled: isClientSubdomain }
  );

  // Em dev OU hub com override de cliente, busca pelo ID
  const { data: tenantById, isLoading: loadingById } = trpc.clients.getById.useQuery(
    { id: devClientId! },
    { enabled: (isDev || isHub) && devClientId !== null }
  );

  // Fallback para dev/preview sem override: usa getActiveConfig (clientId=1)
  const { data: activeConfig, isLoading: loadingConfig } = trpc.clients.getActiveConfig.useQuery(
    undefined,
    { enabled: (isMaster || isHub) && devClientId === null }
  );

  const tenant = useMemo<TenantConfig | null>(() => {
    // Subdomínio real de cliente (produção)
    if (isClientSubdomain && tenantBySubdomain) {
      return {
        ...tenantBySubdomain,
        primaryColor: tenantBySubdomain.primaryColor || "#c14727",
        homeModule: (tenantBySubdomain as any).homeModule ?? null,
      };
    }
    // Dev OU hub com override de cliente específico
    if ((isDev || isHub) && devClientId !== null && tenantById) {
      return {
        id: tenantById.id,
        nome: tenantById.nome,
        slug: tenantById.slug,
        subdomain: tenantById.subdomain ?? tenantById.slug,
        logoUrl: tenantById.logoUrl ?? null,
        logoNegativoUrl: tenantById.logoNegativoUrl ?? null,
        primaryColor: tenantById.primaryColor || "#c14727",
        homeModule: (tenantById as any).homeModule ?? null,
        modules: tenantById.modules,
        networks: tenantById.networks,
      };
    }
    // Hub RAZ ou dev/preview sem override: usa config padrão (todos os módulos)
    if ((isHub || isMaster) && activeConfig) {
      return {
        id: 0, // Hub não é um cliente específico
        nome: "RAZ Hub",
        slug: "hub",
        subdomain: "hub",
        logoUrl: null,
        logoNegativoUrl: null,
        primaryColor: "#c14727",
        homeModule: "dashboard",
        modules: activeConfig.modules,
        networks: activeConfig.networks,
      };
    }
    return null;
  }, [isClientSubdomain, isHub, isMaster, isDev, devClientId, tenantBySubdomain, tenantById, activeConfig]);

  const isLoading = isClientSubdomain
    ? loadingSubdomain
    : (isDev || isHub) && devClientId !== null
      ? loadingById
      : loadingConfig;

  return (
    <TenantContext.Provider value={{ tenant, isLoading, isHub, isMaster, setDevClientId, devClientId, isDev, subdomain }}>
      {children}
    </TenantContext.Provider>
  );
}

export function useTenant() {
  return useContext(TenantContext);
}
